/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/root.reducers';
import {
    GRID_TIMEBAR_REFRESH_TIME,
    IDLE_GRID_GUIDE_DURATION_IN_SECOND,
    MILLISECONDS_IN_A_SECOND,
} from '../../../config';

import './TimeBar.scss';

const TimeBar = (): JSX.Element => {
    const timebarRef = useRef<HTMLDivElement>(null);
    const timebarHeadRef = useRef(new Image());
    const { isPastGuideMode, startTime, currentTimestamp } = useSelector(
        (state: RootState) => state.gridGuide,
    );
    const [timeBarPosition, setTimeBarPosition] = useState(0);

    /**
     * Sets the position of timeBar.
     * timebar-width = 1437px
     * timebar-head-width = 20px
     * margin between timbar and timebar-head = 4px
     * timebar-container-width = timebar-width + (timebar-head-width/2) = 1447px
     * grid-guide-window-duration = 2hr = 2 * 60 * 60 = 7200
     * extraDistance = timebar-head-width + timebar-head
     * pixelPerSecond = (timebar-container-width - extraDistance) / grid-guide-window-duration
     */

    const evaluateCurrentPosition = (pixelPerSecond) => {
        const currentTime = new Date();
        const seconds =
            (currentTime.getTime() - currentTimestamp?.getTime()) / MILLISECONDS_IN_A_SECOND;
        const position = Number(pixelPerSecond * seconds);
        setTimeBarPosition(position);
    };

    useEffect(() => {
        let pixelPerSecond = 0;
        let extraDistance = 0;
        if (timebarHeadRef.current) {
            extraDistance =
                (timebarHeadRef.current?.offsetWidth || 0) +
                parseFloat(getComputedStyle(timebarHeadRef.current).marginLeft);
        }
        if (timebarRef.current) {
            pixelPerSecond =
                (timebarRef.current?.offsetWidth - extraDistance) /
                IDLE_GRID_GUIDE_DURATION_IN_SECOND;
        }

        evaluateCurrentPosition(pixelPerSecond);

        const timeoutId = setInterval(() => {
            evaluateCurrentPosition(pixelPerSecond);
        }, GRID_TIMEBAR_REFRESH_TIME);
        return () => {
            clearInterval(timeoutId);
        };
    }, [startTime]);

    return (
        <React.Fragment>
            {isPastGuideMode ? (
                <div className="timebar-container past-guide-mode">
                    <div className="timebar-past-guide-mode"></div>
                </div>
            ) : (
                <div className="timebar-container" ref={timebarRef}>
                    <div
                        className="timebar"
                        style={{ width: timeBarPosition }}
                        data-testid="guide-timebar"
                    ></div>
                    <img className="timebar-head" ref={timebarHeadRef}></img>
                </div>
            )}
        </React.Fragment>
    );
};

export default TimeBar;
